/** Gameplay one-shots: maps AudioEvents onto themed recipes from the active palette. */
import { sandGust, thunder } from './instruments/nature';
import { crack, crumble, knock, poof, rustle, thud, wash, whistleDrop, whoosh } from './instruments/noisefx';
import type { Synth } from './instruments/synth';
import type { Palette } from './palettes';
import { degreeMidi } from './themes';
import type { AudioEvent, CrashMaterial } from './types';
import { clamp01, finiteOr } from './util';

/** Minimum spacing per event type (s) — protects against accidental per-frame spam. */
const MIN_GAP: Partial<Record<AudioEvent['type'], number>> = {
  uiMove: 0.03,
  launch: 0.08,
  land: 0.05,
  closeCall: 0.05,
  dive: 0.12,
  crash: 0.3,
  fall: 0.5,
  caught: 0.5,
  newBest: 0.5,
  milestone: 0.3,
  gate: 0.2,
  start: 0.25,
};

const UI_WALK = [0, 1, 2, 3, 4, 3, 2, 1];

export class Sfx {
  private uiStep = 0;
  private readonly last: Partial<Record<AudioEvent['type'], number>> = {};

  constructor(
    private readonly s: Synth,
    private palette: Palette,
  ) {}

  setPalette(p: Palette): void {
    this.palette = p;
  }

  play(e: AudioEvent, now: number): void {
    const gap = MIN_GAP[e.type];
    if (gap !== undefined) {
      const prev = this.last[e.type];
      if (prev !== undefined && now - prev < gap && now >= prev) return;
      this.last[e.type] = now;
    }
    const t = now + 0.01;
    switch (e.type) {
      case 'uiMove':
        return this.uiMove(t);
      case 'uiSelect':
        return this.uiSelect(t);
      case 'uiBack':
        return this.uiBack(t);
      case 'start':
        return this.start(t);
      case 'launch':
        return this.launch(t, finiteOr(e.speed, 30));
      case 'land':
        return this.land(t, e.quality, finiteOr(e.combo, 1), clamp01(finiteOr(e.impact, 0.5)));
      case 'closeCall':
        return this.closeCall(t, e.side < 0 ? -1 : 1);
      case 'crash':
        return this.crash(t, e.material);
      case 'bump':
        // Two pearls knocking together: a bright, glassy clack over a soft body thud.
        knock(this.s, t, 900 + e.impact * 500, 0.16 + e.impact * 0.22);
        thud(this.s, t, { f0: 150, f1: 80, dur: 0.12, peak: 0.08 + e.impact * 0.12, noise: 0.2, lp: 900 });
        return;
      case 'fall':
        return this.fall(t);
      case 'caught':
        return this.caught(t);
      case 'newBest':
        return this.newBest(t);
      case 'milestone':
        return this.milestone(t);
      case 'dive':
        return this.dive(t);
      case 'gate':
        return this.gate(t, Math.max(1, Math.floor(finiteOr(e.combo, 1))));
      default:
        return;
    }
  }

  // ───────── UI ─────────

  private uiMove(t: number): void {
    const th = this.palette.theme;
    const deg = UI_WALK[this.uiStep % UI_WALK.length] ?? 0;
    this.uiStep++;
    this.palette.tick(t, degreeMidi(th, deg, th.uiRoot), 0.75, false);
  }

  private uiSelect(t: number): void {
    const th = this.palette.theme;
    this.palette.chime(t, th.chimeRoot, 0.42);
    this.palette.chime(t + 0.085, th.chimeRoot + 7, 0.52);
  }

  private uiBack(t: number): void {
    const th = this.palette.theme;
    this.palette.tick(t, th.uiRoot - 5, 0.85, true);
  }

  // ───────── run ─────────

  private start(t: number): void {
    const p = this.palette;
    const th = p.theme;
    whoosh(this.s, t, { dur: 0.5, f0: 350, f1: 3200, q: 0.9, peak: 0.12, peakAt: 0.8, color: 'pink', send: 0.25 });
    p.flourish(t + 0.34, th.chimeRoot, 0.7);
    p.flourish(t + 0.4, th.chimeRoot + 7, 0.55);
    p.sparkle(t + 0.36, 0.55);
    p.accent(t + 0.34, 0.5);
  }

  private launch(t: number, speed: number): void {
    const sn = clamp01((speed - 12) / 78);
    whoosh(this.s, t, {
      dur: 0.2 + 0.12 * sn,
      f0: 420 + 300 * sn,
      f1: 1700 + 3000 * sn,
      q: 1.1,
      peak: 0.06 + 0.12 * sn,
      peakAt: 0.55,
      color: sn > 0.5 ? 'white' : 'pink',
      send: 0.1,
    });
  }

  private land(t: number, quality: 'perfect' | 'good' | 'rough', comboRaw: number, imp: number): void {
    const p = this.palette;
    const th = p.theme;
    const len = th.scale.length;
    const combo = Math.max(1, Math.floor(comboRaw));
    if (quality === 'perfect') {
      // Tiny-Wings ladder: each consecutive perfect climbs one scale degree, over two octaves.
      const step = combo - 1;
      const d = step % (len * 2);
      const wrapped = step > 0 && d === 0;
      const midi = degreeMidi(th, d, th.chimeRoot);
      const lift = Math.min(1, combo / 10);
      p.chime(t, midi, 0.7 + 0.2 * lift);
      if (combo >= 5) p.chime(t + 0.004, midi - 12, 0.12 + 0.08 * lift, { muted: true });
      p.sparkle(t + 0.03, 0.35 + 0.45 * lift);
      if (wrapped) p.bell(t + 0.09, th.chimeRoot + 12, 0.35);
      thud(this.s, t, { f0: 130, f1: 62, dur: 0.16, peak: 0.06 + 0.1 * imp, noise: 0.4, lp: 400 });
    } else if (quality === 'good') {
      thud(this.s, t, { f0: 120, f1: 58, dur: 0.18, peak: 0.1 + 0.14 * imp, noise: 0.6, lp: 420 });
      p.chime(t + 0.005, degreeMidi(th, (combo - 1) % len, th.chimeRoot), 0.26, { muted: true });
    } else {
      thud(this.s, t, { f0: 95, f1: 45, dur: 0.24, peak: 0.16 + 0.28 * imp, noise: 0.9, lp: 300 });
      p.scuff(t + 0.01, 0.3 + 0.7 * imp);
    }
  }

  private closeCall(t: number, side: -1 | 1): void {
    whoosh(this.s, t, {
      dur: 0.26,
      f0: 3800,
      f1: 650,
      q: 1.6,
      peak: 0.22,
      peakAt: 0.35,
      color: 'white',
      pan: side * 0.85,
      panTo: side * 0.3,
      send: 0.12,
    });
    // Doppler pitch drop, barely there.
    whistleDrop(this.s, t + 0.02, 1250, 600, 0.22, 0.02, side * 0.6, 0.1);
    this.palette.tink(t + 0.07, side * 0.5);
  }

  private crash(t: number, material: CrashMaterial): void {
    const s = this.s;
    switch (material) {
      case 'stone':
        thud(s, t, { f0: 115, f1: 46, dur: 0.38, peak: 0.42, noise: 0.5, lp: 900 });
        crack(s, t, 1500, 1.2, 0.2, 0.025);
        crumble(s, t + 0.03, 0.75, 0.2);
        break;
      case 'wood':
        knock(s, t, 190, 0.38);
        knock(s, t + 0.075, 205, 0.13, 0.15);
        thud(s, t, { f0: 100, f1: 60, dur: 0.2, peak: 0.15, noise: 0.3, lp: 500 });
        break;
      case 'plant':
        rustle(s, t, { dur: 0.55, peak: 0.45, freq: 3200, q: 0.7, rate: 1.6 });
        rustle(s, t + 0.05, { dur: 0.4, peak: 0.24, freq: 1400, q: 0.8, rate: 1.1, pan: -0.2 });
        thud(s, t, { f0: 90, f1: 55, dur: 0.2, peak: 0.14, noise: 0.5, lp: 350 });
        break;
      default:
        thud(s, t, { f0: 110, f1: 50, dur: 0.3, peak: 0.35, noise: 0.5, lp: 600 });
    }
    poof(s, t + 0.04, 0.22);
    this.sadMotif(t + 0.42);
  }

  private sadMotif(t: number): void {
    const p = this.palette;
    const m = p.theme.sadMotif;
    const a = m[0] ?? 74;
    const b = m[1] ?? 71;
    const c = m[2] ?? 69;
    p.pluck(t, a, 0.5);
    p.pluck(t + 0.16, b, 0.45);
    p.pluck(t + 0.34, c, 0.5, { bend: -0.6 });
  }

  private fall(t: number): void {
    const sky = this.palette.theme.id === 'sky';
    whistleDrop(this.s, t, 1700, 260, 1.5, 0.06);
    whoosh(this.s, t, { dur: 1.5, f0: 2600, f1: 300, q: 0.8, peak: 0.1, peakAt: 0.25, color: 'pink', send: 0.3 });
    wash(this.s, t + 0.6, {
      color: sky ? 'pink' : 'brown',
      lp0: 900,
      lp1: 380,
      attack: 0.8,
      hold: 0.6,
      release: 2,
      peak: 0.16,
      send: 0.6,
    });
  }

  private caught(t: number): void {
    const s = this.s;
    const sky = this.palette.theme.id === 'sky';
    const swellT = 0.9;
    wash(s, t, { color: 'brown', lp0: 250, lp1: 2000, attack: swellT, hold: 0.05, release: 0, peak: 0.55, cut: true, send: 0.35 });
    wash(s, t, { color: 'pink', lp0: 400, lp1: 3500, attack: swellT, hold: 0.05, release: 0, peak: 0.25, cut: true, send: 0.35 });
    const hit = t + swellT + 0.05;
    thud(s, hit, { f0: 80, f1: 38, dur: 0.6, peak: 0.35, noise: 0.8, lp: 250, send: 0.4 });
    if (sky) thunder(s, hit, 0.5, 0.9, 0);
    else sandGust(s, hit - 0.05, 0.3, 1.6, 0);
  }

  private newBest(t: number): void {
    const p = this.palette;
    const th = p.theme;
    const run = th.fanfare;
    const step = th.id === 'sky' ? 0.075 : 0.06;
    for (let k = 0; k < run.length; k++) {
      const m = run[k];
      if (m !== undefined) p.flourish(t + k * step, m, Math.min(0.75, 0.45 + 0.05 * k));
    }
    const end = t + run.length * step;
    const top = run[run.length - 1] ?? th.chimeRoot + 12;
    p.chime(end, top, 0.6);
    p.sparkle(end + 0.02, 0.8);
    p.bell(end + 0.05, th.chimeRoot + 12, 0.5);
    p.accent(t, 0.5);
    p.accent(end, 0.75);
  }

  private milestone(t: number): void {
    const th = this.palette.theme;
    this.palette.bell(t, th.chimeRoot + 12, 0.7);
  }

  /** Wind gate: a rushing updraft swell with a rising two-note chime. */
  private gate(t: number, combo: number): void {
    const p = this.palette;
    const th = p.theme;
    whoosh(this.s, t, { dur: 0.7, f0: 600, f1: 4200, q: 1.1, peak: 0.2, peakAt: 0.55, color: 'pink', send: 0.35 });
    const len = th.scale.length;
    const a = degreeMidi(th, (combo + 1) % len, th.chimeRoot);
    const b = degreeMidi(th, (combo + 3) % len, th.chimeRoot + 12);
    p.chime(t + 0.05, a, 0.45);
    p.chime(t + 0.16, b, 0.5);
    p.sparkle(t + 0.12, 0.6);
  }

  private dive(t: number): void {
    thud(this.s, t, { f0: 95, f1: 55, dur: 0.2, peak: 0.07, noise: 0.5, lp: 280 });
  }
}
